import { useMutation } from '@tanstack/react-query'
import dynamicDataConfig from '@/api/config/dynamicDataConfig'
import { mutationKey } from '@/constant/common'
import {
  SCHEMA_NAME_TABLE_SAVED_VIEWS,
  TableSavedViewsBlob,
  TableSavedViewsRecord,
} from '@/types/table-saved-views'

interface SavePayload {
  interfaceId: number | string
  userId: number
  tableKey: string
  /** The record to overwrite; omitted the first time this user saves here. */
  recordId?: string
  views: TableSavedViewsBlob
}

/**
 * Writes the whole blob back — the record holds every view for one grid, so
 * a save, rename, delete or activate is the same call with different content.
 *
 * Returns the stored record so the caller can seed the query cache with it
 * rather than invalidating: a refetch racing this write would put the server's
 * older snapshot back over the one just saved.
 */
const useSaveTableSavedViews = () =>
  useMutation<TableSavedViewsRecord, unknown, SavePayload>(
    async ({ interfaceId, userId, tableKey, recordId, views }) => {
      const id = String(interfaceId)
      const data = { userId, tableKey, views }

      if (recordId) {
        await dynamicDataConfig.updateById(id, recordId, {
          schemaName: SCHEMA_NAME_TABLE_SAVED_VIEWS,
          data,
        })
        return { id: recordId, views }
      }

      const { data: created } = await dynamicDataConfig.create(id, {
        schemaName: SCHEMA_NAME_TABLE_SAVED_VIEWS,
        data,
      })

      return { id: created.id, views }
    },
    { mutationKey: [mutationKey.saveTableSavedViews] },
  )

export default useSaveTableSavedViews
